import React, { useState } from "react";
import {
  AlertTriangle,
  Phone,
  Globe,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  Scale,
  Heart,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Lista nacional de organizaciones pro-bono y red flags que indican
 * cuándo el usuario DEBE buscar abogado humano antes de seguir con la IA.
 * Las cifras telefónicas son públicas, tomadas de los sitios oficiales.
 */
const RED_FLAGS = [
  {
    id: "criminal",
    title: "Antecedentes penales",
    desc: "Si tienes condena, cargos, arresto previo, DUI, violencia doméstica o droga.",
  },
  {
    id: "minor",
    title: "Menor de edad sin un adulto",
    desc: "Si eres menor de 18 años y no estás bajo custodia de un padre o tutor (UAC).",
  },
  {
    id: "prior-removal",
    title: "Orden de deportación previa",
    desc: "Si ya tienes una orden final de deportación de un juez de inmigración.",
  },
  {
    id: "appeal",
    title: "Apelación pendiente en la BIA",
    desc: "Caso en la Board of Immigration Appeals (BIA) o ante una corte federal.",
  },
  {
    id: "trauma",
    title: "Trauma severo o discapacidad mental",
    desc: "Diagnóstico de PTSD, depresión severa o cualquier condición que afecte tu comprensión.",
  },
  {
    id: "state-torture",
    title: "Tortura por agentes del Estado",
    desc: "Si fuiste perseguido por militares, policía o gobierno de tu país de origen.",
  },
  {
    id: "trafficking",
    title: "Trata de personas o violencia política",
    desc: "Víctima de trata, violencia sexual extrema o persecución política prominente.",
  },
  {
    id: "deadline",
    title: "Plazo crítico en menos de 24 horas",
    desc: "Tu audiencia o entrega del formulario es en menos de un día y aún no tienes declaración.",
  },
  {
    id: "family",
    title: "Familia deportada o desaparecida",
    desc: "Cónyuge, hijo o padre ya deportado, secuestrado o desaparecido en tu país.",
  },
];

const PRO_BONO_ORGS = [
  {
    name: "Florence Project",
    region: "Arizona / nacional",
    phone: "520-777-5600",
    web: "firrp.org",
    desc: "Manuales pro-se de asilo y representación gratuita en Arizona.",
    color: "bg-amber-100 text-amber-800",
  },
  {
    name: "National Immigrant Justice Center (NIJC)",
    region: "Illinois / nacional",
    phone: "312-660-1370",
    web: "immigrantjustice.org",
    desc: "Red pro-bono más grande del país. Asilo, detención, apelaciones.",
    color: "bg-blue-100 text-blue-800",
  },
  {
    name: "RAICES",
    region: "Texas / nacional",
    phone: "210-737-0691",
    web: "raicestexas.org",
    desc: "Asilo, detención, defensa de familias y menores.",
    color: "bg-emerald-100 text-emerald-800",
  },
  {
    name: "ASAP — Asylum Seeker Advocacy Project",
    region: "Nacional · gratis",
    phone: "Solicitud en línea",
    web: "asaptogether.org",
    desc: "Membresía gratuita con apoyo legal en línea y emergencias.",
    color: "bg-rose-100 text-rose-800",
  },
  {
    name: "Acacia Center for Justice",
    region: "Red nacional",
    phone: "Solicitud en línea",
    web: "acaciajustice.org",
    desc: "Coordina servicios pro-bono en centros de detención.",
    color: "bg-indigo-100 text-indigo-800",
  },
  {
    name: "AILA Find-a-Lawyer",
    region: "Todos los estados",
    phone: "Buscador en línea",
    web: "aila.org/find-lawyer",
    desc: "Directorio nacional de abogados de inmigración certificados.",
    color: "bg-purple-100 text-purple-800",
  },
  {
    name: "EOIR — Pro Bono Legal Service Providers",
    region: "Oficial DOJ",
    phone: "Lista oficial",
    web: "justice.gov/eoir/list-of-pro-bono-legal-service-providers",
    desc: "Lista oficial del Departamento de Justicia, organizada por estado.",
    color: "bg-zinc-200 text-zinc-800",
  },
];

export default function RedFlagsCard({ defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [showOrgs, setShowOrgs] = useState(false);

  return (
    <div
      data-testid="red-flags-card"
      className="bg-white border-2 border-amber-300 rounded-2xl overflow-hidden shadow-sm"
    >
      <button
        data-testid="red-flags-card-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full p-5 flex items-start gap-3 text-left hover:bg-amber-50/50 transition-colors"
      >
        <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-amber-500 text-white flex items-center justify-center">
          <AlertTriangle className="w-6 h-6" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-700 mb-1">
            Importante · Lee esto primero
          </p>
          <h3 className="text-lg font-black text-zinc-900 leading-tight">
            ¿Cuándo necesitas un abogado humano?
          </h3>
          <p className="text-sm text-zinc-600 leading-snug mt-1">
            Hay 9 situaciones donde la IA no es suficiente. Conócelas para no equivocarte.
          </p>
        </div>
        {open ? (
          <ChevronUp className="w-5 h-5 text-zinc-500 flex-shrink-0 mt-1" />
        ) : (
          <ChevronDown className="w-5 h-5 text-zinc-500 flex-shrink-0 mt-1" />
        )}
      </button>

      {open && (
        <div className="border-t border-amber-200 p-5 space-y-4">
          <p className="text-xs text-zinc-700 leading-snug">
            Si tu situación corresponde a <span className="font-black">cualquiera</span> de los siguientes
            9 casos, la IA <span className="font-black">no debe</span> ser tu única ayuda. Llama a una
            organización pro-bono de inmediato:
          </p>

          <ul className="space-y-2">
            {RED_FLAGS.map((f, i) => (
              <li
                key={f.id}
                data-testid={`red-flag-${f.id}`}
                className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3"
              >
                <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-amber-500 text-white text-xs font-black flex items-center justify-center mt-0.5">
                  {i + 1}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-black text-amber-950 leading-tight">{f.title}</p>
                  <p className="text-xs text-amber-900 leading-snug mt-0.5">{f.desc}</p>
                </div>
              </li>
            ))}
          </ul>

          <Button
            data-testid="red-flags-show-orgs"
            onClick={() => setShowOrgs(true)}
            className="w-full h-12 mt-2 font-black bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl flex items-center justify-center gap-2"
          >
            <Phone className="w-4 h-4" /> Ver organizaciones pro-bono (gratis)
          </Button>
        </div>
      )}

      {showOrgs && <ProBonoDirectoryModal onClose={() => setShowOrgs(false)} />}
    </div>
  );
}

function ProBonoDirectoryModal({ onClose }) {
  return (
    <div
      data-testid="probono-directory-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-xl bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col"
      >
        <div className="relative bg-gradient-to-br from-emerald-700 via-emerald-800 to-teal-900 text-white p-6 pr-14 flex-shrink-0">
          <button
            onClick={onClose}
            data-testid="probono-directory-close"
            className="absolute top-4 right-4 w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-400 text-emerald-950 flex items-center justify-center">
              <Heart className="w-7 h-7" strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-black text-amber-300 mb-1">
                Ayuda legal gratuita
              </p>
              <h2 className="text-2xl font-black leading-tight">Organizaciones pro-bono nacionales</h2>
              <p className="text-sm text-emerald-100 mt-2 leading-snug">
                Todas atienden en español y dan orientación SIN cobrar. Llama o entra a la página
                web. Si una no puede tomar tu caso, te envía a otra.
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {PRO_BONO_ORGS.map((o) => (
            <div
              key={o.name}
              data-testid={`probono-org-${o.name.split(" ")[0].toLowerCase()}`}
              className="border-2 border-zinc-200 rounded-xl p-4"
            >
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${o.color}`}>
                  <Scale className="w-5 h-5" strokeWidth={2.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-black text-zinc-900 leading-tight">{o.name}</p>
                  <p className="text-xs text-zinc-500">{o.region}</p>
                  <p className="text-xs text-zinc-700 mt-1 leading-snug">{o.desc}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
                    <span className="inline-flex items-center gap-1 font-bold text-zinc-900">
                      <Phone className="w-3 h-3" /> {o.phone}
                    </span>
                    <span className="inline-flex items-center gap-1 font-bold text-blue-700">
                      <Globe className="w-3 h-3" /> {o.web}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-zinc-200 bg-zinc-50 p-4 flex-shrink-0">
          <p className="text-[11px] text-zinc-600 leading-snug flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
            <span>
              Estos teléfonos y enlaces son <span className="font-bold">oficiales y públicos</span>.
              miapoyo no recibe comisión por referirte. Tu llamada con ellos es gratis y
              confidencial.
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
